/**
 * Toolbar V2 - Clean, Professional, Icon-based
 * Uses Lucide icons and design system tokens
 */

import { memo } from 'react'
import {
  Undo2,
  Redo2,
  Group,
  Ungroup,
  Lock,
  Unlock,
  BoxSelect,
  Type,
  Hand,
  Grid3x3,
  Magnet,
  Library,
  Settings,
  Download,
  Network,
} from 'lucide-react'
import '../../styles/toolbar-v2.css'

interface ToolbarProps {
  diagramTitle?: string
  diagramType?: string
  onExport?: () => void
  onUndo?: () => void
  onRedo?: () => void
  canUndo?: boolean
  canRedo?: boolean
  onToggleGrid?: () => void
  showGrid?: boolean
  onToggleSnapToGrid?: () => void
  snapToGrid?: boolean
  onToggleNodePalette?: () => void
  onTogglePropertiesPanel?: () => void
  showNodePalette?: boolean
  showPropertiesPanel?: boolean
  onTogglePanMode?: () => void
  isPanMode?: boolean
  onCreateContainer?: () => void
  onCreateTextNode?: () => void
  onGroup?: () => void
  onUngroup?: () => void
  onToggleLock?: () => void
  canGroup?: boolean
  canUngroup?: boolean
  hasSelection?: boolean
  isLocked?: boolean
  onAutoLayout?: () => void
}

function ToolbarV2({
  diagramTitle,
  diagramType,
  onExport,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onToggleGrid,
  showGrid = true,
  onToggleSnapToGrid,
  snapToGrid = false,
  onToggleNodePalette,
  onTogglePropertiesPanel,
  showNodePalette = true,
  showPropertiesPanel = true,
  onTogglePanMode,
  isPanMode = false,
  onCreateContainer,
  onCreateTextNode,
  onGroup,
  onUngroup,
  onToggleLock,
  canGroup = false,
  canUngroup = false,
  hasSelection = false,
  isLocked = false,
  onAutoLayout,
}: ToolbarProps) {
  return (
    <div className="toolbar-v2">
      {/* Left: Branding */}
      <div className="toolbar-section toolbar-left">
        {diagramTitle && (
          <>
            <h1 className="diagram-title">{diagramTitle}</h1>
            {diagramType && (
              <span className="diagram-badge">{diagramType}</span>
            )}
          </>
        )}
      </div>

      {/* Center: Primary Actions */}
      <div className="toolbar-section toolbar-center">
        {/* History Group */}
        <div className="tool-group">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="icon-btn"
            aria-label="Undo"
          >
            <Undo2 size={18} />
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
            className="icon-btn"
            aria-label="Redo"
          >
            <Redo2 size={18} />
          </button>
        </div>

        <div className="divider" />

        {/* Edit Group (Contextual - only show when selection exists) */}
        {hasSelection && (
          <>
            <div className="tool-group">
              {canGroup && onGroup && (
                <button
                  onClick={onGroup}
                  title="Group Selection (Ctrl+G)"
                  className="icon-btn"
                  aria-label="Group"
                >
                  <Group size={18} />
                </button>
              )}
              {canUngroup && onUngroup && (
                <button
                  onClick={onUngroup}
                  title="Ungroup (Ctrl+Shift+G)"
                  className="icon-btn"
                  aria-label="Ungroup"
                >
                  <Ungroup size={18} />
                </button>
              )}
              {onToggleLock && (
                <button
                  onClick={onToggleLock}
                  title={isLocked ? "Unlock (Ctrl+L)" : "Lock (Ctrl+L)"}
                  className="icon-btn"
                  aria-label={isLocked ? "Unlock" : "Lock"}
                >
                  {isLocked ? <Unlock size={18} /> : <Lock size={18} />}
                </button>
              )}
            </div>
            <div className="divider" />
          </>
        )}

        {/* Add Group */}
        <div className="tool-group">
          {onCreateContainer && (
            <button
              onClick={onCreateContainer}
              title="Add Container / Group"
              className="icon-btn"
              aria-label="Add Container"
            >
              <BoxSelect size={18} />
            </button>
          )}
          {onCreateTextNode && (
            <button
              onClick={onCreateTextNode}
              title="Add Text Annotation"
              className="icon-btn"
              aria-label="Add Text"
            >
              <Type size={18} />
            </button>
          )}
        </div>


        {/* Auto Layout */}
        {onAutoLayout && (
          <div className="tool-group">
            <button
              onClick={onAutoLayout}
              title="Auto Layout - Reorganize nodes"
              className="icon-btn"
              aria-label="Auto Layout"
            >
              <Network size={18} />
            </button>
          </div>
        )}

        <div className="divider" />

        {/* View Group */}
        <div className="tool-group">
          <button
            onClick={onTogglePanMode}
            title={isPanMode ? "Switch to Selection Mode" : "Switch to Pan Mode"}
            className={`icon-btn ${isPanMode ? 'active' : ''}`}
            aria-label={isPanMode ? "Selection Mode" : "Pan Mode"}
          >
            <Hand size={18} />
          </button>
          <button
            onClick={onToggleGrid}
            title="Toggle Grid"
            className={`icon-btn ${showGrid ? 'active' : ''}`}
            aria-label="Toggle Grid"
          >
            <Grid3x3 size={18} />
          </button>
          {onToggleSnapToGrid && (
            <button
              onClick={onToggleSnapToGrid}
              title={snapToGrid ? 'Disable Snap to Grid' : 'Enable Snap to Grid'}
              className={`icon-btn ${snapToGrid ? 'active' : ''}`}
              aria-label="Toggle Snap to Grid"
              aria-pressed={snapToGrid}
            >
              <Magnet size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Right: Secondary Actions */}
      <div className="toolbar-section toolbar-right">
        <div className="tool-group">
          <button
            onClick={onToggleNodePalette}
            title="Toggle Node Library (Ctrl+L)"
            className={`icon-btn ${showNodePalette ? 'active' : ''}`}
            aria-label="Toggle Library"
          >
            <Library size={18} />
          </button>
          <button
            onClick={onTogglePropertiesPanel}
            title="Toggle Properties Panel (Ctrl+P)"
            className={`icon-btn ${showPropertiesPanel ? 'active' : ''}`}
            aria-label="Toggle Properties"
          >
            <Settings size={18} />
          </button>
        </div>

        <div className="divider" />

        <button
          onClick={onExport}
          title="Export Diagram"
          className="btn btn-primary btn-sm"
          aria-label="Export"
        >
          <Download size={16} />
          <span>Export</span>
        </button>
      </div>
    </div>
  )
}

export default memo(ToolbarV2)
